import React, { useEffect } from "react";
import { Modal, Button, ModalHeader, Input, FormFeedback, Label } from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const statusOptions = [
  { value: 3, label: "Resolved" },
  { value: 4, label: "Not resolved" },
  { value: 5, label: "Completed" },
];

const ChatCommentModal = ({
  show,
  setShow,
  title,
  onSubmitHandler,
  disableOutsideClick = false,
  size = "md",
  selectedRow = {},
}) => {
  const { t, i18n } = useTranslation();

  // Validation schema
  const validationSchema = Yup.object().shape({
    Status: Yup.number()
      .required(t("Status is required"))
      .typeError(t("Status is required")), // Will make sure it's treated as invalid if empty or incorrect
    Comment: Yup.string().trim().nullable()
  });

  return (
    <Modal
      size={size}
      isOpen={show}
      toggle={() => {
        if (!disableOutsideClick) setShow(false);
      }}
    >
      {/* ********************* MODAL HEADER ***************** */}
      <ModalHeader tag="div" className="modalClose">
        {i18n.language === "ar" ? (
          <div className="d-flex justify-content-between align-items-center w-100">
            <button
              type="button"
              className="btn-close ms-2"
              onClick={() => setShow(false)}
              aria-label="Close"
            />
            <h5 className="modal-title mb-0">{title}</h5>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="modal-title mb-0">{title}</h5>
            <button
              type="button"
              className="btn-close ms-2"
              onClick={() => setShow(false)}
              aria-label="Close"
            />
          </div>
        )}
      </ModalHeader>

      {/* ********************* MODAL BODY ***************** */}
      <Formik
        enableReinitialize
        initialValues={{
          Status: [3, 4, 5].includes(selectedRow?.Status) ? selectedRow?.Status : null,
          Comment: selectedRow?.Comment || null,
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          onSubmitHandler(values); // pass reason & comment
          setSubmitting(false);
          resetForm();
          setShow(false);
        }}
      >
        {({ errors, touched, isSubmitting, isValid, resetForm, values }) => {
          // Reset form when modal closes
          useEffect(() => {
            if (!show) resetForm();
          }, [show, resetForm]);

          return (
            <Form encType="multipart/form-data">
              <div className="modal-body">
                {/* Status Dropdown */}
                <div className="mb-3">
                  <Label for="Status" className="form-label">
                    {t("Status")}
                  </Label>
                  <Field name="Status">
                    {({ field, form }) => (
                      <Input
                        {...field}
                        type="select"
                        id="Status"
                        invalid={touched.Status && !!errors.Status}
                        onChange={(e) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                      >
                        <option value="">{t("SelectStatus")}</option>
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    )}
                  </Field>
                  {touched.Status && errors.Status && (
                    <FormFeedback>{errors.Status}</FormFeedback>
                  )}
                </div>

                {/* Comment Field */}
                <div>
                  <Label for="Comment" className="form-label">
                    {t("Comment")}
                  </Label>
                  <Field name="Comment">
                    {({ field }) => (
                      <Input
                        {...field}
                        type="textarea"
                        rows={5}
                        placeholder={t("Enter your comment")}
                        invalid={touched.Comment && !!errors.Comment}
                      />
                    )}
                  </Field>
                  {touched.Comment && errors.Comment && (
                    <FormFeedback>{errors.Comment}</FormFeedback>
                  )}
                </div>
              </div>

              {/* MODAL FOOTER */}
              <div className="modal-footer d-flex justify-content-center gap-4">
                <Link
                  to="#"
                  className="btn btn-link link-success fw-medium"
                  onClick={() => setShow(false)}
                >
                  <i className="ri-close-line me-1 align-middle"></i> {t("Close")}
                </Link>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || !isValid || !values.Status}
                >
                  <i className="ri-save-3-fill align-bottom me-2"></i>
                  {t("Save Changes")}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ChatCommentModal;